import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { decimalsForValue, formatNumber } from "../../lib/formatNumber";

// Animated glowing stat counter e.g. "100+ Countries"
const StatCounter = ({ value, prefix = "", suffix = "", label, glow = "cyan", compact = false, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const isNumeric = /^-?[0-9.]+$/.test(String(value));
  // Standing number-formatting rule (see CLAUDE.md): above 10, no decimals -
  // round the animation target itself so mid-animation frames don't flash
  // decimals; at or below 10, keep whatever decimal precision was passed in.
  const parsed = isNumeric ? parseFloat(value) : 0;
  const decimals = isNumeric ? decimalsForValue(value) : 0;
  const target = decimals === 0 ? Math.round(parsed) : parsed;
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView || !isNumeric) return;
    const duration = 1400;
    const startTime = performance.now();
    let rafId;
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      setDisplay(progress * target);
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      }
    };
    rafId = requestAnimationFrame(step);
    // Safety net: guarantee the final correct value is shown even if
    // requestAnimationFrame gets throttled (e.g. background tabs).
    const safety = setTimeout(() => setDisplay(target), duration + 400);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(safety);
    };
  }, [isInView, target, isNumeric]);

  const glowClass = glow === "amber" ? "glow-text-amber" : "glow-text-cyan";

  return (
    <motion.div ref={ref} className={className}>
      <div className={`font-data ${compact ? "text-2xl md:text-3xl lg:text-4xl" : "text-4xl md:text-5xl"} font-semibold ${glowClass}`}>
        {prefix}
        {isNumeric ? formatNumber(display, decimals) : value}
        {suffix}
      </div>
      <div className="mt-2 text-xs md:text-sm uppercase tracking-widest text-muted-foreground">{label}</div>
    </motion.div>
  );
};

export default StatCounter;
